import React, { useEffect } from 'react';
import type { IntlShape } from 'react-intl';

import type { FullPageEditorPresetAPI } from '@confluence/editor-presets/entry-points/useFullPageEditorPreset.ts';
import { usePluginStateEffect } from '@atlaskit/editor-common/use-plugin-state-effect';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import {
	type ProviderError,
	type CollabEditProvider,
	type Provider,
} from '@atlaskit/collab-provider';
import type { InlineCommentPluginState } from '@atlaskit/editor-plugin-annotation';

import { useObjectSidebarActions } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { usePageNativeEmbedClient } from '@confluence/embedded-confluence-allowed-features/entry-points/usePageNativeEmbedClient.ts';
import { useConnecting } from '@confluence/native-collab/entry-points/useConnecting.ts';
import { useSchedulePublishFlagsWatcher } from '@confluence/editor-schedule-publish/entry-points/useSchedulePublishFlagsWatcher.ts';
import { useEditorInlineCommentsPubSub } from '@confluence/editor-features/entry-points/useEditorInlineCommentsPubSub.ts';
import { useUnreadCommentsSetup } from '@confluence/unread-comments/entry-points/useUnreadCommentsSetup.ts';
import { useEditorAIReplacingTitle } from '@confluence/editor-features/entry-points/useEditorAIReplacingTitle.ts';
import { useSendContentAgeAnalytics } from '@confluence/editor-features/entry-points/useSendContentAgeAnalytics.ts';
import { useDisabledAnalytics } from '@confluence/editor-features/entry-points/useDisabledAnalytics.ts';
import { useScrollOnUrlChange } from '@confluence/editor-features/entry-points/useScrollOnUrlChange.ts';
import { useSpaceKeyShortcutOverride } from '@confluence/blank-page-quick-actions/entry-points/useSpaceKeyShortcutOverride.ts';
import type { PresetAPIType } from '@confluence/blank-page-quick-actions/entry-points/bpqaConst.ts';
import { useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare.ts';
import { usePrintCoverInjector } from '@confluence/pdf-print-utils/entry-points/coverInjector.ts';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { useProcessedCommentsDataActions } from '@confluence/comments-data-manager/entry-points/useProcessedCommentsData.ts';
import { useMentionsNotificationsActions } from '@confluence/mentions-notifications/entry-points/useMentionsNotifications.ts';
import { useInlineCommentsDispatchContext } from '@confluence/comment-context/entry-points/InlineCommentsContext.ts';
import { useCommentsPanelActions } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { expValEquals, expVal } from '@confluence/feature-experiments';
import { type AddCommentInCacheProps } from '@confluence/comments-data-manager/entry-points/useAllCommentsData.ts';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
type TriggerAnalyticsEventType = (
	eventName: string,
	extraData?: { [key: string]: any },
) => Promise<void>;
import type { CollabHeartbeatError } from '@confluence/native-collab/entry-points/useEditorHeartbeat.ts';

import { useFullPageAutoFocus } from './useFullPageAutoFocus.ts';
import { useFocusedComment } from './useFocusedComment.ts';
import { useEditorCollabConnectivitySync } from './useEditorCollabConnectivitySync.ts';
import { useFullPageEditorLoomInsertion } from './useFullPageEditorLoomInsertion.ts';

export type FullPageEditorSideEffectsProps = {
	// Content identification
	contentId: string;
	contentType: string;
	isLivePage: boolean;
	isUnpublishedDraft: boolean;

	// Title state
	title: string | null;
	draftTitle: string | null;
	isAIEnabledForContent: boolean;
	handleTitleChange: (title: string) => void;

	// Connectivity state
	collabEditInternetLost: boolean;
	internetDisconnected: boolean;
	extractedCollabEditProvider: (CollabEditProvider & Provider) | null;

	// Editor APIs
	fullPageEditorPresetAPI: FullPageEditorPresetAPI | undefined;

	// Comments-related
	isDocumentActive: boolean;
	focusedCommentId: string | undefined;
	replyToCommentId: string | undefined;
	handleUseFocusedCommentGQLError: (error: Error) => void;
	annotationStateRef: React.MutableRefObject<InlineCommentPluginState | undefined>;
	addCommentInCache: (props: AddCommentInCacheProps) => void;
	deleteCommentInCache: (props: { commentId: string; onError?: (error: Error) => void }) => boolean;
	updateUnresolvedInlineCommentFn: (annotationsList: string[]) => void;
	triggerAnalyticsEvent: TriggerAnalyticsEventType;

	// UI state
	proseMirrorRenderedData: Record<string, unknown> | null;
	showBlankPageQuickActions: boolean;
	hasSubmittedCwrPrompt: boolean;
	shouldShowCreateWithRovoExperience: boolean;
	isRovoLLMEnabled: boolean;
	onSpaceKeyPressed: () => void;

	// Feature flags and i18n
	flags: FlagsStateContainer;
	intl: IntlShape;

	// Analytics
	disabled: boolean;
	isTitleFocused: boolean | null;
	shouldRenderPreview: boolean;
	collabEditError: ProviderError | null;
	collabLimitReached: boolean;
	heartbeatError: CollabHeartbeatError | undefined;

	// Annotation/Plugin state
	removeCommentQueryParams: () => void;
	setEditorAnnotations: (annotations: string[], contentId: string) => void;
	getSortedMarkerRefs: (markerRefList: string[], pageMode?: 'view' | 'edit') => string[];
	setOrderedActiveAnnotationIdList: (annotations: string[]) => void;
	editorAnnotationsRef: React.MutableRefObject<string[]>;
};

/**
 * FullPageEditorSideEffects component isolates all side-effect hooks to prevent
 * unnecessary re-renders of the main editor component and its children.
 *
 * By extracting these hooks into a separate component, the main FullPageEditorComponent
 * can re-render without causing all its children to re-render as well.
 *
 * Experiment: platform_editor_isolate_side_effects_experiment
 * This component is only rendered when the experiment flag is enabled.
 */
export const FullPageEditorSideEffects: React.FC<FullPageEditorSideEffectsProps> = ({
	contentId,
	contentType,
	isLivePage,
	isUnpublishedDraft,
	title,
	draftTitle,
	isAIEnabledForContent,
	handleTitleChange,
	collabEditInternetLost,
	internetDisconnected,
	extractedCollabEditProvider,
	fullPageEditorPresetAPI,
	isDocumentActive,
	focusedCommentId,
	replyToCommentId,
	handleUseFocusedCommentGQLError,
	annotationStateRef,
	addCommentInCache,
	deleteCommentInCache,
	updateUnresolvedInlineCommentFn,
	triggerAnalyticsEvent,
	proseMirrorRenderedData,
	showBlankPageQuickActions,
	hasSubmittedCwrPrompt,
	shouldShowCreateWithRovoExperience,
	isRovoLLMEnabled,
	onSpaceKeyPressed,
	flags,
	intl,
	disabled,
	isTitleFocused,
	shouldRenderPreview,
	collabEditError,
	collabLimitReached,
	heartbeatError,
	removeCommentQueryParams,
	setEditorAnnotations,
	getSortedMarkerRefs,
	setOrderedActiveAnnotationIdList,
	editorAnnotationsRef,
}) => {
	// Get locally-scoped actions from their respective contexts/stores
	const { hideObjectSidebar } = useObjectSidebarActions();
	const { resetInitialDataLoadedMap } = useCommentsPanelActions();
	const { clearCommentsData } = useCommentsDataActions();
	const { clearProcessedData } = useProcessedCommentsDataActions();
	const { clearMentionsFlags } = useMentionsNotificationsActions();
	const { setActiveHighlight } = useInlineCommentsDispatchContext();
	const { storedCommentData } = useAnnotations();
	const { setStoredCommentData } = useAnnotationsDispatch();

	// Side effect: Setup native embed client
	usePageNativeEmbedClient();

	// Side effect: Space key shortcut override for CWR
	// Cast to PresetAPIType (which includes typeAhead) — same pattern used in BlankObjectExperienceButtons.
	const isTypeAheadOpen: boolean =
		useSharedPluginStateSelector(fullPageEditorPresetAPI as PresetAPIType, 'typeAhead.isOpen') ??
		false;
	useSpaceKeyShortcutOverride({
		isActive:
			showBlankPageQuickActions && !hasSubmittedCwrPrompt && !shouldShowCreateWithRovoExperience,
		isTypeAheadOpen,
		isRovoLLMEnabled,
		onSpaceKeyPressed,
		isLivePage,
	});

	// Side effect: Expose Google Meet screen share
	useExposeGoogleMeetScreenShare();

	// Side effect: Scroll on URL change
	useScrollOnUrlChange(proseMirrorRenderedData !== null, undefined, fullPageEditorPresetAPI);

	// Side effect: Print cover injector
	usePrintCoverInjector(expValEquals('afm-enable-command-p-reroute', 'isEnabled', true), intl);

	// Side effect: Focused comment handling
	useFocusedComment({
		commentId: focusedCommentId || replyToCommentId,
		contentId,
		handleGqlError: handleUseFocusedCommentGQLError,
		annotationState: annotationStateRef.current,
	});

	// Side effect: Inline comments PubSub
	useEditorInlineCommentsPubSub({
		contentId,
		contentType,
		triggerAnalyticsEvent,
		updateUnresolvedInlineComment: updateUnresolvedInlineCommentFn,
		isDocumentActive,
		storedCommentData,
		setStoredCommentData,
		addCommentInCache,
		deleteCommentInCache,
	});

	// Side effect: Unread comments setup
	useUnreadCommentsSetup({
		pageId: contentId,
		pageContentTypeProps: { pageMode: 'edit' },
	});

	// Side effect: Collab edit connectivity sync
	useEditorCollabConnectivitySync({
		collabEditInternetLost,
		internetDisconnected,
		editorFullPagePresetAPI: fullPageEditorPresetAPI,
	});

	// Side effect: Native collab connecting state
	useConnecting(extractedCollabEditProvider);

	// Side effect: Full page auto focus
	useFullPageAutoFocus(isLivePage);

	// Side effect: AI replacing title
	useEditorAIReplacingTitle({
		enabled: isAIEnabledForContent,
		title: title || draftTitle || '',
		handleNewTitle: handleTitleChange,
		canFireUnifiedAIAnalyticsEvent: true,
	});

	// Side effect: Send content age analytics
	useSendContentAgeAnalytics({ contentId, collabService: 'ncs' });

	// Side effect: Disabled analytics
	useDisabledAnalytics({
		contentId,
		disabled: isTitleFocused || disabled,
		shouldRenderPreview,
		isTitleFocused,
		collabEditError,
		collabLimitReached,
		heartbeatError,
	});

	// Side effect: Schedule publish flags watcher
	useSchedulePublishFlagsWatcher({
		contentId,
		contentType,
		isUnpublishedDraft,
		title: title ?? draftTitle,
		flags,
		isSchedulePublishEnabled: !isLivePage,
	});

	// Side effect: Full page editor loom insertion
	useFullPageEditorLoomInsertion(fullPageEditorPresetAPI);

	// Side effect: Plugin state effect for annotations
	usePluginStateEffect(fullPageEditorPresetAPI as any, ['annotation'], ({ annotationState }) => {
		const annotations = annotationState?.annotations;
		const { selectedAnnotations, mouseData } = annotationState || {};
		annotationStateRef.current = annotationState;

		const handleCommentQueryParams = () => {
			if (
				selectedAnnotations &&
				selectedAnnotations.length > 0 &&
				mouseData?.isSelecting &&
				(focusedCommentId || replyToCommentId)
			) {
				removeCommentQueryParams();
			}
		};

		const setAnnotations = () => {
			const editorAnnotationsToSet = annotations ? Object.keys(annotations) : [];

			setEditorAnnotations(getSortedMarkerRefs(editorAnnotationsToSet, 'edit'), contentId);
			editorAnnotationsRef.current = editorAnnotationsToSet;
			const activeEditorAnnotationsToSet = annotations
				? Object.keys(annotations).filter((key) => annotations[key] === false)
				: [];
			setOrderedActiveAnnotationIdList(getSortedMarkerRefs(activeEditorAnnotationsToSet, 'edit'));
		};

		handleCommentQueryParams();
		setAnnotations();
	});

	// Side effect: Hide object sidebar on content change
	useEffect(() => {
		void hideObjectSidebar();
	}, [contentId, hideObjectSidebar]);

	// Side effect: Comments cleanup on unmount or content change when side effects are isolated
	useEffect(() => {
		return () => {
			resetInitialDataLoadedMap();
			clearCommentsData();
			if (expVal('cc_comments_new_comments_data_manager', 'isEnabled', false)) {
				clearProcessedData();
			}
			clearMentionsFlags();
			setActiveHighlight(null);
		};
	}, [
		contentId,
		resetInitialDataLoadedMap,
		clearCommentsData,
		clearProcessedData,
		clearMentionsFlags,
		setActiveHighlight,
	]);

	return null;
};
